import React from "react";

/**
 * Render DM narration with proper paragraph breaks and inline markdown
 * keyword highlighting in ember-gold.
 *
 * The previous version wrapped the entire body in a single `<span>` which
 * meant double-newlines collapsed into whatever the parent's
 * `whitespace-pre-wrap` rendered — visually flat blocks with no breathing
 * room between beats. The DM is instructed to write in 3 paragraphs; this
 * component now honours that structure by splitting on blank lines and
 * rendering each as its own block with vertical spacing.
 *
 * Supports inline:
 *   • **bold**  → ember-gold semibold with soft glow
 *   • __bold__  → same as above
 *   • *italic*  → italicised, slightly faded
 *   • _italic_  → same as above
 */
function renderInline(text, keyPrefix = "k") {
  if (!text) return null;
  // Combined regex: **bold** | __bold__ | *italic* | _italic_
  // Capturing the marker so we know which style to apply.
  const re = /(\*\*|__)([^*_\n][^*_\n]*?)\1|(\*|_)([^*_\n][^*_\n]*?)\3/g;
  const parts = [];
  let lastIdx = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(<React.Fragment key={`${keyPrefix}-t-${i++}`}>{text.slice(lastIdx, m.index)}</React.Fragment>);
    }
    const isBold = !!m[1];
    const inner = m[2] ?? m[4];
    if (isBold) {
      parts.push(
        <span
          key={`${keyPrefix}-b-${i++}`}
          className="text-amber-300 font-semibold"
          style={{ textShadow: "0 0 8px rgba(230,138,0,0.4)" }}
        >
          {inner}
        </span>
      );
    } else {
      parts.push(
        <em key={`${keyPrefix}-i-${i++}`} className="text-amber-100/85 not-italic">
          <span className="italic">{inner}</span>
        </em>
      );
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    parts.push(<React.Fragment key={`${keyPrefix}-t-${i++}`}>{text.slice(lastIdx)}</React.Fragment>);
  }
  return parts;
}

export function HighlightedText({ text, className = "" }) {
  if (!text) return null;
  // Strip any residual <<TAG>> markers that leaked through server-side
  // sanitisation so the player never sees raw structured tags inline.
  const clean = text.replace(/<<\s*\w+\s*:?[^>]*>>/g, "").trim();
  // Split on blank lines (≥1 newline that is followed by another newline
  // or marks the end of a sentence cluster). Single line-breaks within a
  // paragraph are preserved as soft breaks.
  const blocks = clean.split(/\n{2,}/).map(b => b.trim()).filter(Boolean);
  if (blocks.length === 0) return null;
  return (
    <div className={className}>
      {blocks.map((block, bi) => {
        // Within a block, single newlines become soft line-breaks.
        const lines = block.split(/\n/);
        return (
          <p
            key={`p-${bi}`}
            className={`leading-relaxed ${bi < blocks.length - 1 ? "mb-3.5" : ""}`}
          >
            {lines.map((ln, li) => (
              <React.Fragment key={`l-${bi}-${li}`}>
                {renderInline(ln, `b${bi}-l${li}`)}
                {li < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
